import React from 'react';
import { useTranslation } from 'react-i18next';
import './StudyCard.css';

const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return { hours, minutes };
};

const formatTimeString = (totalSeconds, t) => {
  const { hours, minutes } = formatTime(totalSeconds);
  if (hours > 0 && minutes > 0) {
    return `${hours}${t('share.hourUnit')} ${minutes}${t('share.minuteUnit')}`;
  }
  if (hours > 0) {
    return `${hours}${t('share.hourUnit')}`;
  }
  return `${minutes}${t('share.minuteUnit')}`;
};

const StudyCard = ({ cardType = 'daily', data = {}, theme = 'light', showUsername = false, username = '', cardRef, backgroundImage = null }) => {
  const { t } = useTranslation();
  const { totalSeconds = 0, subjects = [], date = '', goalName = '', percentage = 0, currentStreak = 0, longestStreak = 0, goals = [] } = data;

  const maxSeconds = subjects.length > 0
    ? Math.max(...subjects.map(s => s.seconds))
    : 1;

  const themeClass = `study-card study-card-${theme}${cardType === 'timetable' ? ' study-card-timetable-mode' : ''}`;

  const renderDailyContent = () => (
    <div className="study-card-content">
      <div className="study-card-main-stat">
        {formatTimeString(totalSeconds, t)}
      </div>
      <div className="study-card-sub-stat">
        {t('share.dailyComplete')}
      </div>
      {subjects.length > 0 && (
        <div className="study-card-subjects">
          {subjects.slice(0, 5).map((subject, index) => (
            <div key={index} className="study-card-subject-bar">
              <div
                className="study-card-subject-color"
                style={{ backgroundColor: subject.color }}
              />
              <div className="study-card-subject-info">
                <div className="study-card-subject-name">{subject.name}</div>
                <div className="study-card-subject-bar-track">
                  <div
                    className="study-card-subject-bar-fill"
                    style={{
                      width: `${(subject.seconds / maxSeconds) * 100}%`,
                      backgroundColor: subject.color,
                    }}
                  />
                </div>
              </div>
              <div className="study-card-subject-time">
                {formatTimeString(subject.seconds, t)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderWeeklyContent = () => (
    <div className="study-card-content">
      <div className="study-card-main-stat">
        {formatTimeString(totalSeconds, t)}
      </div>
      <div className="study-card-sub-stat">
        {t('share.weeklyComplete')}
      </div>
      {subjects.length > 0 && (
        <div className="study-card-subjects">
          {subjects.slice(0, 5).map((subject, index) => (
            <div key={index} className="study-card-subject-bar">
              <div
                className="study-card-subject-color"
                style={{ backgroundColor: subject.color }}
              />
              <div className="study-card-subject-info">
                <div className="study-card-subject-name">{subject.name}</div>
                <div className="study-card-subject-bar-track">
                  <div
                    className="study-card-subject-bar-fill"
                    style={{
                      width: `${(subject.seconds / maxSeconds) * 100}%`,
                      backgroundColor: subject.color,
                    }}
                  />
                </div>
              </div>
              <div className="study-card-subject-time">
                {formatTimeString(subject.seconds, t)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderGoalContent = () => {
    const clampedPercentage = Math.min(percentage, 100);
    return (
      <div className="study-card-content">
        <div className="study-card-main-stat">
          {t('share.goalAchieved')}
        </div>
        <div className="study-card-goal-progress">
          <div className="study-card-goal-name">{goalName}</div>
          <div className="study-card-goal-bar-track">
            <div
              className="study-card-goal-bar-fill"
              style={{
                width: `${clampedPercentage}%`,
                backgroundColor: percentage >= 100 ? '#4caf50' : '#2196f3',
              }}
            />
          </div>
          <div className="study-card-goal-percentage">
            {Math.round(percentage)}%
          </div>
        </div>
      </div>
    );
  };

  const renderAchievementContent = () => {
    const maxVal = subjects.length > 0
      ? Math.max(...subjects.map(s => Math.max(s.plannedSeconds || 0, s.actualSeconds || 0)))
      : 1;
    return (
      <div className="study-card-content">
        <div className="study-card-main-stat">
          {t('achievement.title')}
        </div>
        {subjects.length > 0 && (
          <div className="study-card-subjects">
            {subjects.slice(0, 5).map((subject, index) => {
              const rate = subject.plannedSeconds > 0
                ? Math.round((subject.actualSeconds / subject.plannedSeconds) * 100)
                : 0;
              return (
                <div key={index} className="study-card-achievement-row">
                  <div className="study-card-subject-color" style={{ backgroundColor: subject.color }} />
                  <div className="study-card-subject-info">
                    <div className="study-card-subject-name">{subject.name}</div>
                    <div className="study-card-subject-bar-track">
                      <div
                        className="study-card-subject-bar-fill"
                        style={{
                          width: `${maxVal > 0 ? (subject.actualSeconds / maxVal) * 100 : 0}%`,
                          backgroundColor: rate >= 100 ? '#4caf50' : rate >= 70 ? '#ff9800' : '#f44336',
                        }}
                      />
                    </div>
                  </div>
                  <div className="study-card-subject-time" style={{ color: rate >= 100 ? '#4caf50' : rate >= 70 ? '#ff9800' : '#f44336' }}>
                    {rate}%
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderGoalsContent = () => (
    <div className="study-card-content">
      <div className="study-card-main-stat">
        {t('goals.title')}
      </div>
      {goals.length > 0 && (
        <div className="study-card-goals-list">
          {goals.slice(0, 4).map((goal, index) => {
            const clamped = Math.min(goal.percentage, 100);
            const color = goal.percentage >= 100 ? '#4caf50' : goal.percentage >= 70 ? '#ff9800' : '#f44336';
            return (
              <div key={index} className="study-card-goal-item">
                <div className="study-card-goal-row">
                  <span className="study-card-goal-label">{goal.name}</span>
                  <span className="study-card-goal-pct" style={{ color }}>{Math.round(goal.percentage)}%</span>
                </div>
                <div className="study-card-goal-bar-track">
                  <div
                    className="study-card-goal-bar-fill"
                    style={{ width: `${clamped}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderStreakContent = () => (
    <div className="study-card-content">
      <div className="study-card-streak-icon">{'\uD83D\uDD25'}</div>
      <div className="study-card-main-stat">
        {currentStreak}{t('streak.days')}
      </div>
      <div className="study-card-sub-stat">
        {t('streak.currentStreak')}
      </div>
      <div className="study-card-streak-best">
        {t('streak.bestStreak')}: {longestStreak}{t('streak.days')}
      </div>
    </div>
  );

  const renderActualStudyContent = () => {
    const { sessions = [], todos = [] } = data;
    const totalSec = sessions.reduce((sum, s) => sum + (s.seconds || 0), 0);
    const maxSec = sessions.length > 0 ? Math.max(...sessions.map(s => s.seconds || 0)) : 1;
    return (
      <div className="study-card-content">
        <div className="study-card-main-stat">
          {formatTimeString(totalSec, t)}
        </div>
        <div className="study-card-sub-stat">
          {t('actualStudy.title')}
        </div>
        {sessions.length > 0 && (
          <div className="study-card-subjects">
            {sessions.slice(0, 5).map((s, index) => (
              <div key={index} className="study-card-subject-bar">
                <div className="study-card-subject-color" style={{ backgroundColor: s.color }} />
                <div className="study-card-subject-info">
                  <div className="study-card-subject-name">{s.name}</div>
                  <div className="study-card-subject-bar-track">
                    <div
                      className="study-card-subject-bar-fill"
                      style={{ width: `${(s.seconds / maxSec) * 100}%`, backgroundColor: s.color }}
                    />
                  </div>
                </div>
                <div className="study-card-subject-time">
                  {formatTimeString(s.seconds, t)}
                </div>
              </div>
            ))}
          </div>
        )}
        {todos.length > 0 && (
          <div className="study-card-todo-summary" style={{ marginTop: 8, fontSize: '0.85em', opacity: 0.8 }}>
            {t('actualStudy.completedTodo')}: {todos.length}
          </div>
        )}
      </div>
    );
  };

  const renderTimetableContent = () => (
    <div className="study-card-content study-card-timetable">
      <div className="study-card-timetable-img-wrap">
        {data.imageDataUrl && (
          <img src={data.imageDataUrl} alt="timetable" className="study-card-timetable-img" />
        )}
      </div>
      {data.label && (
        <div className="study-card-timetable-label">{data.label}</div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (cardType) {
      case 'weekly':
        return renderWeeklyContent();
      case 'goalAchievement':
        return renderGoalContent();
      case 'achievement':
        return renderAchievementContent();
      case 'goals':
        return renderGoalsContent();
      case 'streak':
        return renderStreakContent();
      case 'actualStudy':
        return renderActualStudyContent();
      case 'timetable':
        return renderTimetableContent();
      case 'daily':
      default:
        return renderDailyContent();
    }
  };

  return (
    <div ref={cardRef} className={themeClass} data-testid="study-card">
      {backgroundImage && (
        <div
          className="study-card-bg-overlay"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}
      {renderContent()}
      {showUsername && username && (
        <div className="study-card-username">@{username}</div>
      )}
      <div className="study-card-footer">
        <span className="study-card-date">{date}</span>
        <span className="study-card-watermark">studyhalltimer.com</span>
      </div>
    </div>
  );
};

export default StudyCard;
